import { AttachmentMenuItem, AttachmentMenuItemType, AttachmentSubmenu } from './attachments-menu'
import { useCallback, useMemo } from 'react'
import { useAttachments, useAttachmentStore } from '../../../../../store'
import { useAvailableSkills } from '../../../../../hooks/useSkills'
import { useTrackAddAttachment, useTrackRemoveAttachment } from '../../../../../hooks/attachments-hooks'
import type { Skill } from '../../../../../types'

const TOOLSET_LABELS: Record<string, string> = {
  linear: 'Linear',
  slack: 'Slack',
  notion: 'Notion',
  cursor: 'Cursor',
}

export const SkillsMenuItem = (item: AttachmentMenuItemType & { onClick?: (e: React.MouseEvent) => void }) => {
  const availableSkills = useAvailableSkills()
  // Respect the per-skill "slash menu" availability toggle — attaching a
  // skill is direct user invocation, the same surface that gate covers.
  const skills = useMemo(() => availableSkills.filter((skill) => skill.availability.userInvoke), [availableSkills])

  return (
    <AttachmentSubmenu item={item}>
      {skills.length === 0 && (
        <AttachmentMenuItem
          id={'not-available'}
          label={<span className={'leading-normal'}>No actions available</span>}
          disabled
          onClick={() => {}}
        />
      )}
      {skills.map((skill) => {
        return <SkillItem key={skill.id} skill={skill} />
      })}
    </AttachmentSubmenu>
  )
}

const SkillItem = ({ skill }: { skill: Skill }) => {
  const { addSkillAttachment, removeAttachment } = useAttachmentStore()
  const attachments = useAttachments()
  const trackAddAttachment = useTrackAddAttachment()
  const trackRemoveAttachment = useTrackRemoveAttachment()

  const attachmentIndex = useMemo(() => {
    return attachments.findIndex((a) => a.pending?.type === 'skill' && a.pending.skillId === skill.id)
  }, [attachments, skill.id])

  const onClick = useCallback<React.MouseEventHandler>(
    (e) => {
      e.stopPropagation()
      if (attachmentIndex === -1) {
        addSkillAttachment(skill)
        trackAddAttachment({ type: 'skill', trigger: 'Attachments Menu' })
      } else {
        removeAttachment(attachmentIndex)
        trackRemoveAttachment({ type: 'skill', trigger: 'Attachments Menu' })
      }
    },
    [skill, attachmentIndex, addSkillAttachment, removeAttachment, trackAddAttachment, trackRemoveAttachment],
  )

  const toolsetLabel = skill.toolset ? (TOOLSET_LABELS[skill.toolset] ?? skill.toolset) : null

  return (
    <AttachmentMenuItem
      id={skill.id}
      label={skill.name}
      description={toolsetLabel}
      selectable={true}
      selected={attachmentIndex !== -1}
      onClick={onClick}
    />
  )
}
